// src/features/tidsregistering/components/absence-planner/AbsenceBar.jsx
import React, { memo } from 'react';
import { Box, useTheme, alpha } from '@mui/material';
import { getDateIndex } from '../../../../components/timeline';
import { TASK_HEIGHT, TASK_SPACING, CATEGORY_PADDING } from '../../../../components/timeline';
import AbsenceBarTooltip from './AbsenceBarTooltip';

/**
 * Renders a single absence as a horizontal bar positioned absolutely
 * within its CategoryRow, mirroring the coordinate system of TaskBar.
 *
 * Approval state drives visual treatment:
 *   approval_wish          → dashed border, reduced opacity
 *   approval_wish_approved → solid, full opacity
 *   approval_wish_rejected → solid, desaturated + strikethrough overlay
 */
function AbsenceBar({ slot, visibleDayIndices, dayWidth, baseDate, level = 0, onClick }) {
  const theme = useTheme();

  const startIndex = getDateIndex(slot.startDate, baseDate);
  const endIndex = getDateIndex(slot.endDate, baseDate);

  const firstVisible = visibleDayIndices[0];
  const lastVisible = visibleDayIndices[visibleDayIndices.length - 1];

  // Cull bars entirely outside the visible window
  if (endIndex < firstVisible || startIndex > lastVisible) return null;

  const clampedStart = Math.max(startIndex, firstVisible);
  const clampedEnd = Math.min(endIndex, lastVisible);

  const leftPx = (clampedStart - firstVisible) * dayWidth;
  const widthPx = Math.max((clampedEnd - clampedStart + 1) * dayWidth - 2, 4);
  const topPx = CATEGORY_PADDING + level * (TASK_HEIGHT + TASK_SPACING);

  const { approvalState } = slot;
  const isWish = approvalState === 'approval_wish';
  const isRejected = approvalState === 'approval_wish_rejected';

  const barSx = {
    position: 'absolute',
    left: leftPx,
    top: topPx,
    width: widthPx,
    height: TASK_HEIGHT,
    bgcolor: isRejected ? alpha(slot.color, 0.35) : alpha(slot.color, isWish ? 0.6 : 0.85),
    border: `1.5px ${isWish ? 'dashed' : 'solid'} ${slot.color}`,
    borderRadius: 1,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    transition: 'opacity 0.15s',
    '&:hover': { opacity: 0.85 },
  };

  return (
    <AbsenceBarTooltip slot={slot}>
      <Box data-absence-bar="true" sx={barSx} onClick={onClick ? () => onClick(slot) : undefined}>
        {isRejected && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              color: theme.palette.error.main,
              fontWeight: 700,
              letterSpacing: 1,
              pointerEvents: 'none',
            }}
          >
            ╌╌╌
          </Box>
        )}
      </Box>
    </AbsenceBarTooltip>
  );
}

export default memo(AbsenceBar);
